import { Tooltip } from '@zendeskgarden/react-tooltips'
import { CancelToken } from 'axios'
import { capitalize } from 'lodash'
import moment from 'moment'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { usePSUser } from '../../hooks'
import { useWindowResize } from '../../hooks/useWindowResize'
import { Item } from '../../models/Item'
import { User } from '../../models/User'
import { generateCancelToken } from '../../modules/apiclient'
import { computeHash } from '../../utils'
import {
    AvailabilityByHash,
    AvailabilityFilter,
    AvailabiltySearchParams,
    CalendarAvailability,
    TimeSlot,
    getAvailabilityByDate,
    getAvailabilityTimeSlots,
    getDateLabelForAvailability,
} from '../../utils/calendar'
import { DATE_KEY_FORMAT } from '../../utils/dates'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { CalendarEmptyState } from './CalendarEmptyState'

type Props = {
    style: React.CSSProperties
    searchParams: AvailabiltySearchParams
    selectedTime: string
    selectedDay: moment.Moment | null
    selectedItem: Item | null | undefined
    selectedProIds: string[]
    pros: User[]
    availabilityByHash: AvailabilityByHash
    availabilityFilter: AvailabilityFilter
    onSelectTimeSlot: (slot: TimeSlot) => void
    onSelectEmployees: () => void
}

export const CalendarAvailabilityContainer = ({
    style,
    searchParams,
    selectedTime,
    selectedDay,
    selectedItem,
    selectedProIds,
    pros,
    availabilityByHash,
    availabilityFilter,
    onSelectTimeSlot,
    onSelectEmployees,
}: Props) => {
    const me = usePSUser()

    const [searching, setSearching] = useState(false)
    const [availability, setAvailability] = useState<CalendarAvailability[]>([])

    const isTeamAccount = useMemo(() => me?.type === 'company', [me?.type])
    const isRecurring = useMemo(
        () => selectedItem?.type === 'class' && selectedItem?.classType !== 'dropin',
        [selectedItem]
    )
    const duration: number = useMemo(
        () => availabilityFilter.duration.value,
        [availabilityFilter.duration.value]
    )
    const isOpenEndedReservation = useMemo(
        () => selectedItem?.type === 'reservation' && !duration,
        [selectedItem, duration]
    )
    const needEndDate = useMemo(
        () => !!selectedDay && isOpenEndedReservation,
        [selectedDay, isOpenEndedReservation]
    )

    const headerTitle = useMemo(() => {
        if (!selectedDay) return 'Select start date'
        if (needEndDate) return 'Select end date'
        if (!!selectedDay && !needEndDate)
            return selectedItem?.name ? selectedItem.name : 'No service'
        return ''
    }, [selectedDay, needEndDate, selectedItem])

    const headerSubtitle = useMemo(() => {
        if (!selectedDay || needEndDate) return 'Add your dates for your reservation'
        if (!!selectedDay && !needEndDate) return getDateLabelForAvailability(selectedDay, duration)
        return ''
    }, [selectedDay, needEndDate, duration])

    const searchCalendarAvailability = (
        params: AvailabiltySearchParams,
        hash: string,
        cancelToken: CancelToken
    ) => {
        console.log('[todo] searchCalendarAvailability', params, hash, cancelToken)
        setSearching(false)
    }

    useEffect(() => {
        if (!selectedDay) return

        setSearching(true)

        const hash = Math.abs(computeHash(JSON.stringify(searchParams))).toString()
        if (hash in availabilityByHash) {
            const availabilityByDate = getAvailabilityByDate(availabilityByHash[hash])
            const selectedAvailability = availabilityByDate[selectedDay.format(DATE_KEY_FORMAT)]
            setAvailability(selectedAvailability ?? [])
            setSearching(false)
            return
        }

        const cancelToken = generateCancelToken()
        searchCalendarAvailability(searchParams, hash, cancelToken.token)

        return () => {
            if (cancelToken) {
                cancelToken.cancel('cancel search availability for selected day')
            }
        }
    }, [searchParams, availabilityByHash, selectedDay])

    const sessionCountLabel = useMemo(() => {
        if (!selectedItem || selectedItem.type !== 'class' || selectedItem.classType === 'dropin')
            return ''

        const count =
            selectedItem.classType === 'series'
                ? selectedItem.lessonCountString || selectedItem.instances
                : selectedItem.classCount

        return !!count ? `(${count} ${count == 1 ? 'session' : 'sessions'})` : ''
    }, [selectedItem])

    const hasFilters = useMemo(
        () =>
            availabilityFilter.pros.length > 0 ||
            availabilityFilter.services.length > 0 ||
            availabilityFilter.duration.value,
        [availabilityFilter]
    )

    const timeSlots = useMemo(
        () =>
            !!selectedDay
                ? getAvailabilityTimeSlots(availability, selectedDay, selectedItem?.type ?? '')
                : {},
        [availability, selectedDay, selectedItem]
    )

    const availabilityContainerBodyRef = useRef<HTMLDivElement>(null)
    const { width } = useWindowResize()
    const [maxEmployees, setMaxEmployees] = useState(0)

    useLayoutEffect(() => {
        if (!availabilityContainerBodyRef.current) return
        const PADDING_OFFSET = 320
        const EMPLOYEE_WIDTH = 44
        const availableWidth = availabilityContainerBodyRef.current.clientWidth - PADDING_OFFSET
        const maxAvatars = Math.floor(availableWidth / EMPLOYEE_WIDTH)
        setMaxEmployees(maxAvatars > 1 ? maxAvatars - 1 : 0)
    }, [width])

    return (
        <div
            className="flex max-w-[calc(100%-387px)] flex-1 flex-col rounded-lg bg-white"
            style={style}
        >
            <div className="flex flex-col gap-1 px-8 py-4">
                <Label size="lg" className="text-gray-700">
                    {headerTitle}
                </Label>
                <Paragraph className="flex items-center gap-2 text-gray-600">
                    {headerSubtitle}
                    {!!sessionCountLabel && <div>{sessionCountLabel}</div>}
                    {isRecurring && <Icon icon="repeat.small" />}
                </Paragraph>
            </div>

            <div
                className="flex h-[calc(100%-73px)] flex-col gap-2 overflow-x-auto overflow-y-auto overscroll-contain px-8 py-6"
                ref={availabilityContainerBodyRef}
            >
                {searching && (
                    <div style={{ textAlign: 'center', padding: '128px 0' }}>
                        <Spinner />
                    </div>
                )}

                {!searching && !isOpenEndedReservation && Object.keys(timeSlots).length === 0 && (
                    <CalendarEmptyState
                        src={'/icons/deadline.svg'}
                        title={`No ${hasFilters ? 'results' : 'available time slots'}`}
                        subtitle={`Try to select ${hasFilters ? 'different filters' : 'a different date'}`}
                    />
                )}

                {!searching &&
                    Object.entries(timeSlots).map(([label, slots]) => (
                        <div className="flex flex-col gap-2" key={label}>
                            <Label className="text-gray-600">
                                {['class', 'reservation'].includes(label)
                                    ? 'Check-in time'
                                    : capitalize(label)}
                            </Label>

                            <div
                                className="mx-0 my-4 flex gap-2"
                                style={{
                                    flexDirection: pros.length > 0 ? 'column' : 'row',
                                    flexWrap: pros.length > 0 ? 'nowrap' : 'wrap',
                                }}
                            >
                                {slots?.map((slot) => (
                                    <div key={slot.time} className="flex items-center gap-6">
                                        {slot.time !== selectedTime && (
                                            <Button
                                                className={`w-[232px] min-w-[232px] ${!slot.available ? 'border-0 bg-gray-100 text-gray-500 hover:bg-gray-300' : ''} `}
                                                onClick={() => onSelectTimeSlot(slot)}
                                            >
                                                {slot.time}
                                            </Button>
                                        )}

                                        {slot.time === selectedTime && (
                                            <Button
                                                className="w-[232px] min-w-[232px]"
                                                intent="primary"
                                                onClick={() => onSelectTimeSlot(slot)}
                                            >
                                                {slot.time}
                                            </Button>
                                        )}

                                        {!!selectedItem?.type &&
                                            ['class', 'reservation'].includes(selectedItem.type) &&
                                            slot.annotation && (
                                                <Label className="flex items-center gap-1">
                                                    <div className="text-gray-700">
                                                        {/* TODO: use remaining_capacity when ready */}
                                                    </div>
                                                    <div className="text-gray-600">
                                                        {slot.annotation}
                                                    </div>
                                                </Label>
                                            )}

                                        {isTeamAccount && (
                                            <Tooltip
                                                content="Edit Pros"
                                                size="medium"
                                                style={{
                                                    display:
                                                        slot.time === selectedTime
                                                            ? 'inline-block'
                                                            : 'none',
                                                    background: '#3C94A4',
                                                    borderRadius: 8,
                                                    fontSize: 13,
                                                }}
                                            >
                                                <div
                                                    className="flex min-w-0 items-center gap-1"
                                                    style={{
                                                        cursor:
                                                            slot.time === selectedTime
                                                                ? 'pointer'
                                                                : 'default',
                                                    }}
                                                    onClick={() =>
                                                        slot.time === selectedTime
                                                            ? onSelectEmployees()
                                                            : null
                                                    }
                                                >
                                                    {slot.employees
                                                        .slice(0, maxEmployees)
                                                        .map((employee) => (
                                                            <div
                                                                key={employee.id}
                                                                className="flex min-w-[40px] flex-col items-center gap-[3px]"
                                                            >
                                                                {!!employee.id && (
                                                                    <div
                                                                        className={`rounded-full border-[2px] ${slot.time === selectedTime && selectedProIds.includes(employee.id) ? 'border-success-400' : 'border-white'}`}
                                                                    >
                                                                        <ContactAvatar
                                                                            user={employee}
                                                                            size="xs"
                                                                        />
                                                                    </div>
                                                                )}
                                                                {!!employee.name && (
                                                                    <div className="w-full truncate text-center text-[10px]/[13px] font-medium">
                                                                        {
                                                                            employee.name.split(
                                                                                ' '
                                                                            )[0]
                                                                        }
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}

                                                    {slot.employees.slice(maxEmployees).length >
                                                        0 && (
                                                        <div className="flex min-w-[40px] flex-col items-center gap-[3px]">
                                                            <div className="rounded-full border-[2px] border-white">
                                                                <div className="bg-primary-700 flex size-6 max-w-full items-center justify-center rounded-full text-[10px] text-white">
                                                                    +
                                                                    {
                                                                        slot.employees.slice(
                                                                            maxEmployees
                                                                        ).length
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className="w-full truncate text-center text-[10px]/[13px] font-medium">
                                                                More
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Tooltip>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    )
}
